import React, { useEffect, useRef, useState } from "react";
import PropTypes from 'prop-types';
import _uniqueId from 'lodash/uniqueId';
import { Icon, Typography } from "../../index";
import './text_area.scss';

export const TextArea = ({
    autoFocus = false,
    colorStyle = 'neutral',
    disabled = false,
    error = false,
    hintMsg,
    id,
    label,
    maxLength,
    minLength,
    name,
    onBlur = () => { },
    onChange = () => { },
    onFocus = () => { },
    required = false,
    rows = "5",
    value,
    ...inputProps
}) => {

    const [enableClearBtn, setEnableClearBtn] = React.useState();
    const [hasValue, setHasValue] = React.useState(value);
    const [isFocused, setIsFocused] = React.useState(false);

    const inputRef = useRef();
    const [uId] = useState(_uniqueId('text-field-'));
    const fieldId = id ? id : uId;
    const labelText = label ? `${label}${!required ? ' (Optional)' : ''}` : '';
    const showClearBtn = (isFocused && hasValue) || ((value || hasValue) && (enableClearBtn));
    const _colorStyle = error ? "error" : colorStyle;
    const timeoutRef = useRef();

    useEffect(() => {
        return () => {
            clearTimeout(timeoutRef.current);
        };
    }, []);

    useEffect(() => {
        setHasValueStyle();
    }, [value]);

    const handleOnBlur = (event) => {
        setIsFocused(false);

        if (event.relatedTarget?.className !== 'bsw-text-area__clear-btn') {
            clearTimeout(timeoutRef.current);
            const clearBtnTimeout = setTimeout(() => { setEnableClearBtn(false) }, 100);
            timeoutRef.current = clearBtnTimeout;
        }

        onBlur(event);
    }

    const handleOnChange = (event) => {
        setHasValueStyle();

        if (onChange) {
            onChange(event);
        }
    }

    const handleOnFocus = (event) => {
        setIsFocused(true);
        setEnableClearBtn(true);

        onFocus(event);
    }

    const setHasValueStyle = () => {
        if (inputRef.current.value && inputRef.current.value?.length > 0) {
            setHasValue(true);
            if (isFocused) {
                setEnableClearBtn(true);
            } else {
                setEnableClearBtn(false);
            }
        } else {
            setHasValue(false);
            setEnableClearBtn(false);
        }
    }

    const clearInput = (event) => {
        if (inputRef.current) {
            inputRef.current.value = "";
            inputRef.current.focus();
        }

        handleOnChange({
            event, target: {
                name: inputRef.current.name ? inputRef.current.name : "",
                value: ""
            }
        });
    }

    const stateStyle = {
        error: {
            iconName: 'exclamation',
            iconColor: '$color-system-b1',
            msgColor: '$color-system-b1',
        },
        neutral: {
            iconName: '',
            iconColor: '',
            msgColor: '$color-secondary-a2',
        },
        success: {
            iconName: 'check',
            iconColor: '$color-system-a1',
            msgColor: '$color-system-a1',
        },
    };

    const textAreaClasses = [
        `bsw-text-area`,
        `bsw-text-area-color--${_colorStyle}`,
        `${disabled ? 'bsw-text-area--disabled' : ''}`,
        `${isFocused ? 'bsw-text-area--focused' : ''}`,
        `${hasValue ? 'bsw-text-area--filled' : ''}`,
    ].join(' ');

    return (
        <>
            <div className={textAreaClasses}>
                <textarea
                    {...inputProps}
                    autoFocus={autoFocus}
                    id={fieldId}
                    name={name}
                    className="bsw-input"
                    type="text"
                    placeholder={labelText}
                    disabled={disabled}
                    value={value}
                    ref={inputRef}
                    required={required}
                    rows={rows}
                    maxLength={maxLength}
                    minLength={minLength}
                    onBlur={handleOnBlur}
                    onChange={handleOnChange}
                    onFocus={handleOnFocus}
                />
                {maxLength && < div className="bsw-text-area__count">
                    <Typography type="meta-01" color="$color-secondary-a2">{maxLength - (inputRef.current?.value?.length || 0)}</Typography>
                </div>}
                {labelText && <label className="bsw-text-area__label" htmlFor={fieldId}>
                    <Typography
                        type={(isFocused || value || hasValue) ? "meta-01" : "body-02"}
                        color="$color-secondary-a2"
                    >
                        {labelText}
                    </Typography>
                </label>}
                <div className="bsw-text-area__end">
                    {showClearBtn &&
                        <button
                            type="button"
                            onClick={clearInput}
                            className="bsw-text-area__clear-btn"
                            aria-label={`Clear ${label}`}
                            onFocus={() => setEnableClearBtn(true)}
                            onBlur={() => setEnableClearBtn(false)}
                        >
                            <Icon name="x" color="$color-primary-a2" size="medium" />
                        </button>
                    }
                </div>
                {hintMsg &&
                    <div className="bsw-text-area__hint">
                        {(_colorStyle !== 'neutral') &&
                            <div className="bsw-text-area__validation-icon">
                                <Icon
                                    name={stateStyle[_colorStyle]['iconName']}
                                    color={stateStyle[_colorStyle]['iconColor']}
                                    size="small"
                                />
                            </div>
                        }
                        <Typography type="meta-01" color={stateStyle[_colorStyle]['msgColor']}>
                            {hintMsg}
                        </Typography>
                    </div>
                }
            </div>
        </>
    )
}

TextArea.propTypes = {
    autoFocus: PropTypes.bool,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    colorStyle: PropTypes.oneOf(['error', 'neutral', 'success']),
    hintMsg: PropTypes.string,
    id: PropTypes.string,
    label: PropTypes.string,
    maxLength: PropTypes.number,
    minLength: PropTypes.number,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    required: PropTypes.bool,
    rows: PropTypes.string,
    value: PropTypes.string,
}